// frontend/src/scenes/admin/Organizations.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import AdminBackButton from "./AdminBackButton";
import { authGet, authPost, getUser } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.orgs)) return resp.orgs;
  if (Array.isArray(resp.organizations)) return resp.organizations;
  return [];
}

export default function AdminOrganizations() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const [form, setForm] = useState({ name: "", code: "" });
  const [selectedOrgId, setSelectedOrgId] = useState("");

  const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
  const canCreateOrg = ["owner", "global_admin", "superadmin"].includes(role);
  const selectedOrg = useMemo(() => rows.find((r) => r.id === selectedOrgId) || null, [rows, selectedOrgId]);

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const j = await authGet("/api/admin/organizations");
      const nextRows = pickRows(j);
      setRows(nextRows);
      if (!selectedOrgId && nextRows.length) setSelectedOrgId(nextRows[0].id);
    } catch (e) {
      setErr(e.message || "Failed to load organizations.");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOrg = async () => {
    if (!canCreateOrg) return;
    setBusy(true);
    setErr("");
    try {
      const payload = { name: (form.name || "").trim(), code: (form.code || "").trim() || null };
      if (!payload.name) throw new Error("Organization name is required.");

      const created = await authPost("/api/admin/organizations", payload);
      const createdId = created?.row?.id;

      setForm({ name: "", code: "" });
      setToast({ open: true, msg: "Organization created.", severity: "success" });
      await load();
      if (createdId) setSelectedOrgId(createdId);
    } catch (e) {
      setErr(e.message || "Create failed.");
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 1, minWidth: 260 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
      { field: "code", headerName: "Code", width: 140 },
      { field: "created_at", headerName: "Created", width: 220 },
    ],
    []
  );

  return (
    <Box sx={{ flex: "1 1 auto", minHeight: 0, minWidth: 0, height: "100%", overflow: "auto", p: { xs: 1, md: 2 }, pb: 4 }}>
      <AdminBackButton />
      <Header title="Organizations" subtitle="Create and select lab organizations" />

      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Create Organization</Typography>
              {!canCreateOrg ? <Alert severity="info" sx={{ mb: 1 }}>Only Global Admins can create new organizations. Org admins and readers see assigned organizations only.</Alert> : null}
              <TextField label="Name" fullWidth size="small" sx={{ mb: 1 }} value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
              <TextField label="Code (optional)" fullWidth size="small" sx={{ mb: 2 }} value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))} />
              <Button variant="contained" onClick={createOrg} disabled={busy || !canCreateOrg}>Create</Button>
              <Button variant="text" onClick={load} disabled={busy} sx={{ ml: 1 }}>Refresh</Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="subtitle1">Organization Management</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This page is only for organization records. Report Settings and Departments now have their own Admin pages.
              </Typography>
              {selectedOrg ? (
                <Typography variant="body2"><strong>Selected:</strong> {selectedOrg.name}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">No organization selected.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={busy}
              getRowId={(r) => r.id}
              rowSelectionModel={selectedOrgId ? [selectedOrgId] : []}
              onRowClick={(params) => setSelectedOrgId(params.row.id)}
              disableRowSelectionOnClick={false}
            />
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
